import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SingleSelectBox from "../../../components/FormComponents/SingleSelectBox";
import { useAxiosContext } from "../../../context/AxiosContext";

export default function DBGroups({ selectedConnectorId }) {
  const { orgId } = useParams();

  const { axiosInstance, handleError } = useAxiosContext();
  const [allGroups, setAllGroups] = useState([]);
  const [dbUsers, setDbUsers] = useState([]);
  const [userGroupAssignments, setUserGroupAssignments] = useState({});

  // Modify the useEffect where you're setting userGroupAssignments
  useEffect(() => {
    const getConnector = async () => {
      try {
        const { data } = await axiosInstance.get(`/dbconnector/${selectedConnectorId}`);

        // Set DB users and initialize group assignments
        if (data.users && data.users.length > 0) {
          setDbUsers(data.users);

          // Initialize userGroupAssignments with the group object, not just the ID
          const assignments = {};
          data.users.forEach((user) => {
            if (user.assigned_group) {
              // Find the group object that matches the assigned_group ID
              const groupObject = allGroups.find((group) => group.id === user.assigned_group);
              assignments[user.user_id] = groupObject || null;
            } else if (user.assigned_groups && user.assigned_groups.length > 0) {
              // Fallback to the old structure if it exists
              assignments[user.user_id] = user.assigned_groups[0];
            } else {
              assignments[user.user_id] = null;
            }
          });
          setUserGroupAssignments(assignments);
        }
      } catch (error) {
        handleError(error);
      }
    };

    if (selectedConnectorId && allGroups.length > 0) {
      getConnector();
    }
  }, [selectedConnectorId, axiosInstance, handleError, allGroups]);

  // Fetch all available groups
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await axiosInstance.get(`/user-service/groups/org/${orgId}`);
        if (response.data && response.data.length > 0) {
          setAllGroups(response.data);
        }
      } catch (error) {
        handleError(error);
      }
    };

    fetchGroups();
  }, [orgId, axiosInstance, handleError]);

  // Function to get available groups for a specific user
  const getAvailableGroups = (currentUserId) => {
    // Get all groups currently assigned to other users
    const assignedGroups = Object.entries(userGroupAssignments)
      .filter(([userId]) => userId !== currentUserId) // Exclude the current user
      .map(([, group]) => group)
      .filter((group) => group !== null); // Remove null assignments

    // Filter out groups that are assigned to other users
    return allGroups.filter((group) => !assignedGroups.some((assigned) => assigned.id === group.id));
  };

  // Handle group selection change
  const handleGroupChange = async (userId, selectedGroup) => {
    try {
      // PATCH request to update the assigned groups on the server
      await axiosInstance.patch(`/dbuser/${userId}/connector/${selectedConnectorId}/group`, {
        assigned_group: selectedGroup.id ? selectedGroup.id : ""
      });

      // Update local state if patch was successful
      setUserGroupAssignments((prev) => ({
        ...prev,
        [userId]: selectedGroup // If SingleSelectBox expects an array, you might use: [selectedGroup]
      }));
    } catch (error) {
      // Handle error accordingly
      handleError(error);
    }
  };

  return (
    <div className="dbuser-container">
      <h2 className="dbuser-heading">DB Users and Group Assignments</h2>

      <table className="dbuser-table">
        <thead className="dbuser-thead">
          <tr>
            <th className="dbuser-th">Groups</th>

            <th className="dbuser-th">DB Users</th>
          </tr>
        </thead>
        <tbody>
          {dbUsers.map((user) => (
            <tr key={user.user_id} className="dbuser-tr">
              <td className="dbuser-td">
                <div className="dbuser-group-container">
                  <div className="dbuser-select-box-wrapper">
                    <SingleSelectBox
                      options={getAvailableGroups(user.user_id)}
                      onChange={(selected) => handleGroupChange(user.user_id, selected)}
                      selectedData={userGroupAssignments[user.user_id] || null}
                      placeholder="Select Group"
                      className="dbuser-placeholder"
                    />
                  </div>
                </div>
              </td>
              <td className="dbuser-td">{user.username}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
